import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Paper,
  Typography,
  Container,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
} from '@mui/material';
import Card from '../components/UIKit/Card';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { coursesAPI, testsAPI, achievementsAPI, leaderboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GradeIcon from '@mui/icons-material/Grade';
import { useTranslation } from 'react-i18next';
import { getUserAvatarSrc } from '../utils/avatarUtils';

// Регистрируем компоненты для Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Компонент для отображения рейтинга пользователя
const LeaderboardWidget = () => {
  const [loading, setLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const { t } = useTranslation(['dashboard', 'common']);
  const { user } = useAuth();

  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        
        if (!user || !user._id) {
          console.log('Пользователь не авторизован или нет ID:', user);
          setError('Пользователь не авторизован');
          setLoading(false);
          return;
        }
        
        console.log('Запрашиваем данные рейтинга для ID:', user._id);
        const response = await leaderboardAPI.getUserRanking(user._id);
        console.log('Ответ API рейтинга:', response);
        
        if (response.data && response.data.rankings) {
          // Обрабатываем данные - добавляем информацию об уровне из API
          const processedData = response.data.rankings.map(item => {
            // Проверяем, является ли этот пользователь текущим
            const isCurrentUser = item._id === user._id;
            
            // Добавляем уровень из user объекта, если это текущий пользователь
            return {
              ...item,
              level: isCurrentUser ? user.level || 'N/A' : item.level || 'N/A',
              isCurrentUser
            };
          });
          
          setLeaderboardData(processedData);
        } else {
          setError('Данные не получены');
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных рейтинга:', error);
        setError(error.message || 'Ошибка запроса');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();
  }, [user]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={40} />
      </Paper>
    );
  }

  return (
    <Card sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
        {t('dashboard:leaderboard.title')}
      </Typography>
      
      {leaderboardData.length > 0 ? (
        <List disablePadding>
          {leaderboardData.map((user, index) => (
            <React.Fragment key={user._id || index}>
              <ListItem
                sx={{
                  backgroundColor: user.isCurrentUser ? theme.palette.action.selected : 'transparent',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 30, fontWeight: 'bold' }}>
                  #{user.rank}
                </Typography>
                <ListItemAvatar>
                  <Avatar src={getUserAvatarSrc(user)} alt={user.username}>
                    {user.username?.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={user.username} 
                  secondary={`XP: ${user.experience} • Уровень: ${user.level}`}
                />
              </ListItem>
              {index < leaderboardData.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Box textAlign="center" py={3}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error ? `${t('dashboard:leaderboard.no_data')} (${error})` : t('dashboard:leaderboard.no_data')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID пользователя: {user?._id || 'не найден'}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const { t } = useTranslation(['dashboard', 'common']);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    courseProgress: [],
    testResults: [],
    achievements: {},
    learningTime: [],
  });
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (!user) {
          console.log('Пользователь не авторизован');
          setLoading(false);
          return;
        }

        // Параллельно запрашиваем все необходимые данные
        const [courseProgress, testStats, achievementStats] = await Promise.all(
          [
            coursesAPI.getUserProgress(),
            testsAPI.getStatistics(),
            achievementsAPI.getStats(),
          ]
        );

        // Исправление для корректной обработки данных
        const testResultsData = testStats.data?.testResults || [];
        const achievementsData = achievementStats.data || {};

        console.log('Загружены данные для дашборда. UserId:', user._id);
        
        // Устанавливаем данные
        setStats({
          courseProgress: courseProgress.data || [],
          testResults: testResultsData,
          achievements: achievementsData,
          learningTime: (courseProgress.data || []).map((course) => ({
            name: course.title,
            time: course.timeSpent || 0,
          })),
        });
      } catch (error) {
        console.error('Ошибка при загрузке данных для дашборда:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Данные для графика прогресса курсов
  const courseProgressData = {
    labels: Array.isArray(stats.courseProgress)
      ? stats.courseProgress.map((course) => course.title)
      : [],
    datasets: [
      {
        label: t('dashboard:charts.progress_label'),
        data: Array.isArray(stats.courseProgress)
          ? stats.courseProgress.map((course) => course.progress)
          : [],
        backgroundColor: theme.palette.primary.main,
        borderRadius: 2,
      },
    ],
  };

  // Update Bar chart options for course progress
  const courseProgressOptions = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: (value) => `${value}%`,
          stepSize: 20,
        },
      },
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (context) => stats.courseProgress[context[0].dataIndex].title,
          label: (context) =>
            `${t('dashboard:tooltips.progress')}: ${context.parsed.y}%`,
        },
      },
    },
  };

  // Данные для графика результатов тестов
  const testResultsData = {
    labels: Array.isArray(stats.testResults)
      ? stats.testResults.map((test) => test.title)
      : [],
    datasets: [
      {
        label: t('dashboard:charts.test_results_label'),
        data: Array.isArray(stats.testResults)
          ? stats.testResults.map((test) => test.score)
          : [],
        backgroundColor: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        borderWidth: 2,
        tension: 0.4,
        fill: false,
      },
    ],
  };

  // Options for test results chart
  const testResultsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: (value) => `${value}%`,
          stepSize: 20,
        },
      },
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (context) => stats.testResults[context[0].dataIndex].title,
          label: (context) => {
            const test = stats.testResults[context.dataIndex];
            return [
              `${t('dashboard:tooltips.score')}: ${test.score.toFixed(0)}%`,
              `${t('dashboard:tooltips.status')}: ${
                test.passed
                  ? t('dashboard:tooltips.passed')
                  : t('dashboard:tooltips.failed')
              }`,
              `${t('dashboard:tooltips.time_spent')}: ${Math.floor(
                test.timeSpent / 60
              )}${t('dashboard:tooltips.minutes')} ${test.timeSpent % 60}${t(
                'dashboard:tooltips.seconds'
              )}`,
            ];
          },
        },
      },
    },
  };

  // Данные для графика достижений
  const achievementsData = {
    labels: [
      t('dashboard:charts.achievements_earned'),
      t('dashboard:charts.achievements_remaining'),
    ],
    datasets: [
      {
        label: t('dashboard:charts.achievements'),
        data: [
          stats.achievements.earned || 0,
          stats.achievements.total
            ? stats.achievements.total - stats.achievements.earned
            : 0,
        ],
        backgroundColor: [
          theme.palette.tertiary.main,
          theme.palette.action.disabled,
        ],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  // Styles for metric cards
  const metricCardStyles = {
    padding: 3,
    borderRadius: '16px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.common.white,
  };

  const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    mb: 2,
  };

  const valueStyles = {
    fontSize: '2.5rem',
    fontWeight: 500,
    lineHeight: 1.2,
  };

  // Вычисление общего времени обучения
  const totalLearningTime = stats.courseProgress?.reduce(
    (total, course) => total + (course.timeSpent || 0), 
    0
  ) || 0;
  
  // Вычисление средней оценки по тестам
  const averageTestScore = 
    stats.testResults?.length > 0
      ? Math.round(
          stats.testResults.reduce((sum, test) => sum + (test.score || 0), 0) / 
          stats.testResults.length
        )
      : 0;

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ my: 4 }}>
      <Typography component="h1" variant="h3" sx={{ mb: 4 }}>
        {t('dashboard:title')}
      </Typography>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              ...metricCardStyles,
              background: theme.palette.primary.main,
            }}
          >
            <Box sx={headerStyles}>
              <Typography variant="body1">
                {t('dashboard:metrics.courses_started')}
              </Typography>
              <SchoolIcon sx={{ flexShrink: 0 }} />
            </Box>
            <Typography sx={valueStyles}>
              {stats.courseProgress?.length || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              ...metricCardStyles,
              background: theme.palette.secondary.main,
            }}
          >
            <Box sx={headerStyles}>
              <Typography variant="body1">
                {t('dashboard:metrics.average_progress')}
              </Typography>
              <TrendingUpIcon sx={{ flexShrink: 0 }} />
            </Box>
            <Typography sx={valueStyles}>
              {stats.courseProgress?.length > 0
                ? Math.round(
                    stats.courseProgress.reduce(
                      (sum, course) => sum + (course.progress || 0),
                      0
                    ) / stats.courseProgress.length
                  )
                : 0}
              %
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              ...metricCardStyles,
              background: theme.palette.success.main,
            }}
          >
            <Box sx={headerStyles}>
              <Typography variant="body1">
                {t('dashboard:metrics.tests_completed')}
              </Typography>
              <AssignmentIcon sx={{ flexShrink: 0 }} />
            </Box>
            <Typography sx={valueStyles}>
              {stats.testResults?.length || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              ...metricCardStyles,
              background: theme.palette.tertiary.main,
            }}
          >
            <Box sx={headerStyles}>
              <Typography variant="body1">
                {t('dashboard:metrics.achievements')}
              </Typography>
              <EmojiEventsIcon sx={{ flexShrink: 0 }} />
            </Box>
            <Typography sx={valueStyles}>
              {stats.achievements?.earned || 0}/{stats.achievements?.total || 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Новый ряд для дополнительной статистики */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              ...metricCardStyles,
              background: theme.palette.info.main,
            }}
          >
            <Box sx={headerStyles}>
              <Typography variant="body1">
                {t('dashboard:metrics.learning_time')}
              </Typography>
              <AccessTimeIcon sx={{ flexShrink: 0 }} />
            </Box>
            <Typography sx={valueStyles}>
              {Math.floor(totalLearningTime / 60)}ч {totalLearningTime % 60}м
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              ...metricCardStyles,
              background: theme.palette.warning.main,
            }}
          >
            <Box sx={headerStyles}>
              <Typography variant="body1">
                {t('dashboard:metrics.average_score')}
              </Typography>
              <GradeIcon sx={{ flexShrink: 0 }} />
            </Box>
            <Typography sx={valueStyles}>
              {averageTestScore}%
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Графики */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'medium' }}>
              {t('dashboard:charts.course_progress')}
            </Typography>
            <Box
              sx={{
                height: 300,
                maxWidth: '100%',
                position: 'relative',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  minWidth: courseProgressData.labels.length * 60 + 'px',
                  pb: 2,
                }}
              >
                <Bar
                  data={courseProgressData}
                  options={courseProgressOptions}
                />
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'medium' }}>
              {t('dashboard:charts.test_results_trend')}
            </Typography>
            <Box
              sx={{
                height: 300,
                maxWidth: '100%',
                position: 'relative',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  minWidth: testResultsData.labels.length * 20 + 'px',
                  pb: 2,
                }}
              >
                <Line data={testResultsData} options={testResultsOptions} />
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('dashboard:charts.achievements')}
            </Typography>
            <Box
              sx={{ height: 300, display: 'flex', justifyContent: 'center' }}
            >
              <Pie data={achievementsData} options={{ ...options }} />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <LeaderboardWidget />
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
